import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Returns whether the current user has designer access (course/workspace admin).
 * - Admins (invited to platform with admin role): always designers.
 * - Users with owned project, course membership (owner/editor), or workspace: designers.
 * - Learners (only enrolled in courses, no project/workspace): not designers → /learn.
 * - Others (no project, no enrollments): designers so they can create one course and download.
 */
export function useIsDesigner(userId: string | undefined) {
  const { data: isDesigner, isLoading } = useQuery({
    queryKey: ["is-designer", userId ?? ""],
    queryFn: async (): Promise<boolean> => {
      if (!userId) return false;

      const [
        { data: adminRow },
        { data: ownedProject },
        { data: courseMember },
        { data: workspace },
        { data: workspaceMember },
        { data: enrollments },
      ] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").limit(1).maybeSingle(),
        supabase.from("learning_projects").select("id").eq("user_id", userId).limit(1).maybeSingle(),
        supabase.from("course_members").select("id").eq("user_id", userId).in("role", ["owner", "editor"]).limit(1).maybeSingle(),
        supabase.from("workspaces").select("id").eq("user_id", userId).limit(1).maybeSingle(),
        supabase.from("workspace_members").select("id").eq("user_id", userId).limit(1).maybeSingle(),
        supabase.from("user_course_enrollments").select("id").eq("user_id", userId).limit(1).maybeSingle(),
      ]);

      if (adminRow?.role === "admin") return true;
      if (ownedProject || courseMember || workspace || workspaceMember) return true;
      if (enrollments) return false; // learner: only enrollments, no designer role
      return true; // other: no project yet, allow to create one course
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });

  return { isDesigner: isDesigner ?? false, isLoading: !!userId && isLoading };
}
